import type { CaptureResult } from 'posthog-js'
import { apiClient } from '@/lib/api/api-client'
import { useOrganizationStore } from '@/store/organization.store'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

import type { User } from '@scrambl/shared'
import type {
  ActivationMilestone,
  ProductEventName,
  ProductEventProperties,
  TrackedFeature,
} from '@scrambl/shared/product-analytics'
import { projectProductEvent } from '@scrambl/shared/product-analytics'

import { projectPosthogEvent } from './analytics-privacy'
import { projectAnalyticsUrl } from './analytics-routes'
import { prepareDesktopPosthogScript } from './desktop-posthog-scripts'
import {
  createDiagnosticAssetResolver,
  readDiagnosticAssets,
} from './diagnostic-assets'
import { createWebPrivacyHooks } from './sentry-privacy'

export { ACTIVATION_MILESTONES } from '@scrambl/shared/product-analytics'
export type {
  ActivationMilestone,
  TrackedFeature,
} from '@scrambl/shared/product-analytics'

type AnalyticsUser = Pick<User, 'id' | 'role'>

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
  'https://us.i.posthog.com'
const APP_URL =
  (import.meta.env.VITE_APP_URL as string | undefined) ??
  'http://localhost:3005'
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const APP_ENV =
  (import.meta.env.VITE_APP_ENV as string | undefined) ??
  (import.meta.env.PROD ? 'production' : 'development')
const APP_VERSION = import.meta.env.VITE_APP_VERSION as string | undefined
const POSTHOG_CAPTURE =
  APP_ENV === 'production' ||
  (
    import.meta.env.VITE_POSTHOG_CAPTURE as string | undefined
  )?.toLowerCase() === 'true'
const IS_TAURI = typeof window !== 'undefined' && '__TAURI__' in window
const APP_PLATFORM = IS_TAURI ? 'desktop' : 'web'
const APP_RUNTIME = IS_TAURI ? 'tauri' : 'browser'

let posthogReady = false
let sentryReady = false

// One contract protects custom events at both vendor and server boundaries.
export type EventName = ProductEventName
export type EventProperties = ProductEventProperties

/** Route classes only: query/fragment values and account/record slugs are private. */
export function sanitizeAnalyticsUrl(value: string): string {
  return projectAnalyticsUrl(value, APP_URL)
}

/** Protect custom payloads and known route/profile metadata after SDK enrichment. */
export function sanitizeAnalyticsEvent(
  event: CaptureResult | null,
): CaptureResult | null {
  return projectPosthogEvent(event, APP_URL, getAnalyticsSuperProperties())
}

function getAnalyticsUrl(path?: string): string {
  const fallbackPath =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : '/'

  try {
    return new URL(path ?? fallbackPath, APP_URL).toString()
  } catch {
    return path ?? fallbackPath
  }
}

function getAnalyticsSuperProperties(): EventProperties {
  return {
    app_platform: APP_PLATFORM,
    app_runtime: APP_RUNTIME,
    app_environment: APP_ENV,
    app_version: APP_VERSION,
  }
}

function normalizeDesktopEvent(
  event: CaptureResult | null,
): CaptureResult | null {
  if (!event || !IS_TAURI) return event

  const path =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : undefined
  const analyticsUrl = getAnalyticsUrl(path)

  let host: string | undefined
  try {
    host = new URL(analyticsUrl).host
  } catch {
    host = undefined
  }

  return {
    ...event,
    properties: {
      ...event.properties,
      ...getAnalyticsSuperProperties(),
      $current_url: analyticsUrl,
      $host: host,
      $pathname:
        typeof window !== 'undefined' ? window.location.pathname : undefined,
    },
  }
}

export function initAnalytics(): void {
  if (POSTHOG_KEY && !posthogReady) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      api_transport: 'fetch',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      mask_all_text: true,
      mask_all_element_attributes: true,
      enable_recording_console_log: false,
      persistence: IS_TAURI ? 'localStorage' : 'localStorage+cookie',
      cross_subdomain_cookie: !IS_TAURI,
      prepare_external_dependency_script: IS_TAURI
        ? prepareDesktopPosthogScript
        : undefined,
      session_recording: {
        // Explicit production budget; override when investigating a rollout.
        sampleRate: (() => {
          const value = Number(
            import.meta.env.VITE_POSTHOG_REPLAY_SAMPLE_RATE ?? '0.1',
          )
          return Number.isFinite(value) && value >= 0 && value <= 1
            ? value
            : 0.1
        })(),
        maskAllInputs: true,
        maskTextSelector: '*',
      },
      disable_session_recording: false,
      before_send: event =>
        sanitizeAnalyticsEvent(normalizeDesktopEvent(event)),
      loaded: ph => {
        if (!POSTHOG_CAPTURE) {
          ph.opt_out_capturing()
        } else {
          ph.opt_in_capturing()
        }
      },
    })
    posthog.register(getAnalyticsSuperProperties())
    posthogReady = true
  }

  if (SENTRY_DSN && !sentryReady) {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: APP_ENV,
      release: APP_VERSION,
      tracesSampleRate: APP_ENV === 'production' ? 0.1 : 1.0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      sendDefaultPii: false,
      enableLogs: false,
      enableMetrics: false,
      ...createWebPrivacyHooks({
        environment: APP_ENV,
        release: APP_VERSION,
        resolveAsset: createDiagnosticAssetResolver(
          APP_URL,
          typeof document === 'undefined'
            ? undefined
            : readDiagnosticAssets(document),
        ),
      }),
      // Replays are what turn an unreproducible bug report into something we
      // can watch. Text and media are masked because these sessions carry real
      // customer data.
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
          networkDetailAllowUrls: [],
          networkCaptureBodies: false,
          networkRequestHeaders: [],
          networkResponseHeaders: [],
        }),
      ],
    })
    sentryReady = true
  }
}

export function identify(user: AnalyticsUser, orgId: string | null): void {
  if (!user?.id) return

  if (posthogReady) {
    posthog.identify(user.id, {
      role: user.role,
    })
    if (orgId) {
      posthog.group('organization', orgId)
    } else if (Object.keys(posthog.getGroups()).length) {
      posthog.resetGroups()
    }
  }

  if (sentryReady) {
    Sentry.setUser({ id: user.id })
    Sentry.setTag('organization_id', orgId ?? undefined)
  }
}

export function resetAnalytics(): void {
  if (posthogReady) {
    posthog.reset()
  }
  if (sentryReady) {
    Sentry.setUser(null)
    Sentry.setTag('organization_id', undefined)
  }
  clearActivationMilestones()
}

export function track(event: EventName, properties?: EventProperties): void {
  const projected = projectProductEvent(event, properties)
  if (!projected) return
  if (posthogReady) {
    posthog.capture(projected.event, {
      ...getAnalyticsSuperProperties(),
      ...projected.properties,
    })
  }
  void sendServerEvent(projected.event, projected.properties)
}

async function sendServerEvent(
  event: string,
  properties?: EventProperties,
): Promise<void> {
  try {
    await apiClient('/user-events/track', {
      method: 'POST',
      body: { eventType: event, payload: properties ?? {} },
      // Onboarding can emit events before a workspace exists. Once selected,
      // use the same workspace, partner and desktop authentication as the app.
      skipOrgContext: !useOrganizationStore.getState().currentOrganization?.id,
      silentAuthFailure: true,
      timeoutMs: 5_000,
      keepalive: true,
    })
  } catch {
    // best-effort — never block user flow on telemetry
  }
}

// Cheap per-session suppression so a burst of the same milestone (e.g. three
// tasks created in a row) doesn't drown the funnel. Deliberately naive: the
// authoritative "first time ever" stamp is server-side, not here.
const ACTIVATION_SESSION_KEY = 'scrambl.activation_milestones'
const firedMilestones = new Set<string>()
let firedMilestonesRestored = false

function restoreFiredMilestones(): void {
  if (firedMilestonesRestored) return
  firedMilestonesRestored = true
  if (typeof window === 'undefined') return
  try {
    const raw = window.sessionStorage.getItem(ACTIVATION_SESSION_KEY)
    if (!raw) return
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      parsed.forEach(value => {
        if (typeof value === 'string') firedMilestones.add(value)
      })
    }
  } catch {
    // Storage unavailable or corrupt — fall back to in-memory only.
  }
}

function persistFiredMilestones(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      ACTIVATION_SESSION_KEY,
      JSON.stringify([...firedMilestones]),
    )
  } catch {
    // Storage unavailable — in-memory suppression still applies.
  }
}

function clearActivationMilestones(): void {
  firedMilestones.clear()
  firedMilestonesRestored = true
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(ACTIVATION_SESSION_KEY)
  } catch {
    // Nothing to do — the in-memory set is already cleared.
  }
}

/**
 * Emit one step of the activation funnel. Fires at most once per milestone per
 * browser session; call it freely from every authoritative moment.
 */
export function trackActivationMilestone(
  milestone: ActivationMilestone,
  properties?: EventProperties,
): void {
  const projected = projectProductEvent('activation_milestone', properties, {
    milestone,
  })
  if (!projected) return
  restoreFiredMilestones()
  if (firedMilestones.has(milestone)) return
  firedMilestones.add(milestone)
  persistFiredMilestones()

  track(projected.event, projected.properties)
}

/** Emit `feature_used` for one of the adoption-tracked features. */
export function trackFeatureUsed(
  feature: TrackedFeature,
  properties?: EventProperties,
): void {
  const projected = projectProductEvent('feature_used', properties, { feature })
  if (projected) track(projected.event, projected.properties)
}

export function trackPageview(path: string): void {
  if (!posthogReady) return
  posthog.capture('$pageview', {
    ...getAnalyticsSuperProperties(),
    $current_url: sanitizeAnalyticsUrl(getAnalyticsUrl(path)),
  })
}
